import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TourStep {
  target: string;
  title: string;
  body: string;
  placement: 'bottom' | 'top' | 'left' | 'right';
}

const STORAGE_KEY = 'monigo:admin-tour-dismissed';

function getTourSteps(t: (key: string) => string): TourStep[] {
  return [
    {
      target: '[data-tour="sidebar"]',
      title: t('tour.sidebarTitle'),
      body: t('tour.sidebarBody'),
      placement: 'right',
    },
    {
      target: '[data-tour="dashboard"]',
      title: t('tour.dashboardTitle'),
      body: t('tour.dashboardBody'),
      placement: 'bottom',
    },
    {
      target: '[data-tour="customers"]',
      title: t('tour.customersTitle'),
      body: t('tour.customersBody'),
      placement: 'right',
    },
    {
      target: '[data-tour="kyc"]',
      title: t('tour.kycTitle'),
      body: t('tour.kycBody'),
      placement: 'right',
    },
    {
      target: '[data-tour="transfers"]',
      title: t('tour.transfersTitle'),
      body: t('tour.transfersBody'),
      placement: 'right',
    },
    {
      target: '[data-tour="rates"]',
      title: t('tour.ratesTitle'),
      body: t('tour.ratesBody'),
      placement: 'right',
    },
    {
      target: '[data-tour="promo-codes"]',
      title: t('tour.promoCodesTitle'),
      body: t('tour.promoCodesBody'),
      placement: 'right',
    },
  ];
}

export function GuidedTour() {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const steps = getTourSteps(t);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      // Wait a moment for the UI to render
      const timer = setTimeout(() => setActive(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    if (step >= steps.length) return;
    const current = steps[step]!;

    const el = document.querySelector(current.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      const placement = current.placement;
      setPosition({
        top: rect.top + window.scrollY + (placement === 'bottom' ? rect.height + 12 : placement === 'top' ? -12 : rect.height / 2),
        left: rect.left + window.scrollX + (placement === 'right' ? rect.width + 12 : placement === 'left' ? -12 : rect.width / 2),
      });
    }
  }, [step, active, steps]);

  if (!active || step >= steps.length || !steps[step]) return null;

  const current = steps[step]!;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-[999] bg-black/30 backdrop-blur-sm"
        onClick={() => setActive(false)}
      />

      {/* Tooltip card */}
      <div
        className="fixed z-[1000] max-w-sm rounded-xl border border-border bg-surface p-5 shadow-2xl"
        style={{ top: position.top, left: position.left }}
      >
        {/* Progress dots */}
        <div className="mb-3 flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-6 bg-primary' : 'w-1.5 bg-border'
              }`}
            />
          ))}
        </div>

        <h3 className="text-lg font-semibold text-text">{current.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-text-muted">{current.body}</p>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              localStorage.setItem(STORAGE_KEY, 'true');
              setActive(false);
            }}
            className="text-sm text-text-muted underline hover:text-text"
          >
            {t('tour.skip')}
          </button>

          <div className="flex gap-2">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text hover:bg-surface-elevated"
              >
                {t('tour.back')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                if (step === steps.length - 1) {
                  localStorage.setItem(STORAGE_KEY, 'true');
                  setActive(false);
                } else {
                  setStep((s) => s + 1);
                }
              }}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              {step === steps.length - 1 ? t('tour.done') : t('tour.next')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
